/**
 * The visit page's card — the board's own (folder 50, Boards 1, 13, 14, 15):
 * white (or `surface-internal` for money), a 1 px line, radius 14; a header of
 * 14 / 18 / 12 with the title at 14.5 / 700, its count *· 3* in the muted
 * weight, the lowercase sub, and one green link on the right, over a
 * `line-faint` rule. On a phone (Board 16) the header is 12 / 14 with the
 * count in the title and no rule — the rows carry their own.
 *
 * It is the board's rather than the record shell's `RecordCard` because the
 * two are drawn to different numbers (10 px header padding there, 14 here),
 * and the board is the spec (law 1).
 */
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cn } from '../lib/cn.js'
import { COMMON } from './copy.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'

export interface CardAction {
  label: string
  glyph?: VisitGlyphName
  onClick?: () => void
  href?: string
}

export function BoardCard({
  title, count, sub, chip, action, right, internal = false, rail = false, phone = false, className,
  asDrawn, children, bodyClassName,
}: {
  title: string
  /** *· 3* — drawn in the title's size, muted. */
  count?: number
  /** The lowercase sub on the title's line. */
  sub?: string
  /** The purple *Internal* chip, after the title. */
  chip?: ReactNode
  action?: CardAction | null
  /** Anything else the header carries at its right (Ready's progress). */
  right?: ReactNode
  /** `surface-internal` — the money cards. */
  internal?: boolean
  /** A rail card: the header's gap is 6 rather than 8. */
  rail?: boolean
  /** Board 16's phone card. */
  phone?: boolean
  className?: string
  asDrawn?: string
  bodyClassName?: string
  children: ReactNode
}) {
  return (
    <section
      data-as-drawn={asDrawn}
      className={cn('box-border overflow-hidden rounded-record border border-line',
        internal ? 'bg-surface-internal' : 'bg-surface', className)}
    >
      <header
        className={cn('flex items-center',
          phone ? 'gap-2 px-3.5 py-3' : 'border-b border-line-faint pb-3 pl-[18px] pr-[18px] pt-3.5',
          !phone && (rail ? 'gap-1.5' : 'gap-2'))}
      >
        <b className="whitespace-nowrap text-[14.5px] font-bold">{title}</b>
        {count !== undefined && !phone && (
          <span className="text-[14.5px] font-medium text-text-muted">{COMMON.count(String(count))}</span>
        )}
        {chip}
        {sub && <span className="min-w-0 truncate text-[12px] text-text-muted">{sub}</span>}
        <span className="flex-1" />
        {right}
        {action && <CardLink action={action} />}
      </header>
      <div className={bodyClassName}>{children}</div>
    </section>
  )
}

/** A card header's green link — *Open the schedule*, *Money tab*, *Job’s visits*, *See all*. */
export function CardLink({ action, className }: { action: CardAction; className?: string }) {
  const inner = (
    <>
      {action.glyph && <VisitGlyph name={action.glyph} size={13} stroke={2.2} />}
      {action.label}
    </>
  )
  const style = cn('inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold text-accent hover:text-accent-dark',
    className)
  return action.href
    ? <Link to={action.href} className={style}>{inner}</Link>
    : <button type="button" onClick={action.onClick} className={style}>{inner}</button>
}

/** The board's 30 px row button (*Order*, *Add a note for the crew*, *Adjust*) — white, or green. */
export function SmallButton({
  children, glyph, primary = false, onClick, className, height = 30,
}: {
  children: ReactNode
  glyph?: VisitGlyphName
  primary?: boolean
  onClick?: () => void
  className?: string
  /** 30 (Board 1's rows), 28 (People's Call · Text, Review), 32 (*Open the invoice draft*). */
  height?: 28 | 30 | 32
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ height }}
      className={cn('inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button border px-3.5 text-[12px] font-semibold',
        primary ? 'border-accent bg-accent text-white hover:bg-accent-dark' : 'border-line bg-surface text-text hover:bg-hover',
        className)}
    >
      {glyph && <VisitGlyph name={glyph} size={14} stroke={2} />}
      {children}
    </button>
  )
}
